import { useState } from "react";
import { useWizard } from "components/elements/Wizard/useWizard";
import { getModelTextSearch } from "lib/actions/getModelTextSearch";
import { nameof } from "lib/actions/util/nameof";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { SpeckleImportModel } from "../types/SpeckleImportModel";
import { SpeckleProjectModel } from "../types/SpeckleProjectModel";
import Loading from "components/elements/Loading/Loading";
import FormError from "components/elements/Forms/FormError";
import SpeckleTitle from "../SpeckleTitle";
import InputSearch from "components/elements/Input/InputSearch";
import SpeckleRadioInputProjects from "../controls/SpeckleRadioInputProjects";
import SpeckleRefreshButton from "../SpeckleRefreshButton";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../SpeckleBreadcrumbs";
import { useSpeckleWorkspaces } from "components/modules/UploadDialog/lib/hooks/useSpeckleWorkspaces";
import {
  getClearedValuesForStep,
  SpeckleImportStep,
} from "components/modules/UploadDialog/SpeckleImportWizard/steps/getClearedValuesForStep";

export default function SpeckleImportWorkspaceStep() {
  const [searchTerm, setSearchTerm] = useState("");
  const { values, isSubmitting, setValues, submitForm, setTouched, setErrors } =
    useFormikContext<SpeckleImportModel>();
  const { stepJump } = useWizard();
  const parsedConnectedAccountId = parseInt(values.connectedAccountId);
  const { speckleWorkspaces, isLoading, hasError, refresh } =
    useSpeckleWorkspaces(
      isNaN(parsedConnectedAccountId) ? undefined : parsedConnectedAccountId,
    );

  function handleStepJump(step: SpeckleImportStep) {
    const clearedValues = getClearedValuesForStep(step, values);
    setValues(clearedValues, false);
    setTouched({}, false);
    setErrors({});
    stepJump(step);
  }

  const { search } = getModelTextSearch<SpeckleProjectModel>(
    searchTerm,
    (project) => project.name,
  );
  const workspaces = search(speckleWorkspaces) || [];
  const workspaceItems =
    !isLoading &&
    !hasError &&
    workspaces.map((x) => ({
      value: x.model.id,
      label: x.model.name,
    }));

  function onRefresh() {
    refresh();
  }

  async function onSelect(value: string) {
    setValues(
      (values) => ({
        ...values,
        workspaceId: value,
        workspaceName: workspaces.find((x) => x.model.id === value)?.model.name,
      }),
      false,
    );
    await new Promise((res) => setTimeout(res, 0)); //hack: cannot submit immediately after setting values
    submitForm();
  }

  return (
    <div className="grid grid-cols-2 gap-8">
      <div>
        <header>
          <SpeckleTitle />
          <h1 className="mt-10 text-2xl font-thin leading-loose">
            Select a <span className="font-normal">Speckle Workspace</span>
          </h1>
        </header>
      </div>
      <div className="space-y-5">
        <SpeckleBreadcrumbs>
          <SpeckleCrumb onClick={() => handleStepJump(0)}>
            {values.connectedAccountId}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb active>Workspaces</SpeckleCrumb>
        </SpeckleBreadcrumbs>
        <div className="flex items-center justify-between pr-5 text-xs">
          <span>{speckleWorkspaces?.length ?? 0} workspaces available</span>
          <SpeckleRefreshButton onClick={onRefresh} />
        </div>
        <div className="h-[36rem] select-none space-y-8 overflow-y-auto pb-8 pr-4">
          <InputSearch
            defaultValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search workspaces"
          />
          <Field
            name={nameof(values, "workspaceId")}
            as={SpeckleRadioInputProjects}
            items={workspaceItems}
            onSelect={onSelect}
            disabled={isSubmitting}
          />
          {isLoading && <Loading />}
          {hasError && <span className="text-center">Error</span>}
        </div>
        <ErrorMessage
          name={nameof(values, "workspaceId")}
          component={FormError}
        />
      </div>
    </div>
  );
}
